import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { Search, X, Clock, ChevronDown, ChevronUp } from 'lucide-react'

export default function Pointage() {
  const [coureurs, setCoureurs] = useState([])
  const [courses, setCourses] = useState([])
  const [points, setPoints] = useState([])
  const [coursePoints, setCoursePoints] = useState([])
  const [detections, setDetections] = useState([])
  const [loading, setLoading] = useState(true)

  const [onglet, setOnglet] = useState('point')
  const [filterCourse, setFilterCourse] = useState('')
  const [selectedPoint, setSelectedPoint] = useState(null)
  const [triAsc, setTriAsc] = useState(false)
  const [popupPoint, setPopupPoint] = useState(null)
  const [ongletPoint, setOngletPoint] = useState('arrives')

  const [search, setSearch] = useState('')
  const [selectedCoureur, setSelectedCoureur] = useState(null)
  const [showRaw, setShowRaw] = useState(null)
  const [saisie, setSaisie] = useState({ date: new Date().toISOString().split('T')[0], heure: '' })

  const [reglesCourse, setReglesCourse] = useState('')
  const [reglesPoint, setReglesPoint] = useState('')
  const [reglesCourseTarget, setReglesCourseTarget] = useState('')
  const [selectedCoureurs, setSelectedCoureurs] = useState([])

  useEffect(() => {
    fetchAll()
    const sub = supabase
      .channel('pointage-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'detections' }, fetchAll)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'coureurs' }, fetchAll)
      .subscribe()
    return () => supabase.removeChannel(sub)
  }, [])

  async function fetchAll() {
    const [c, co, p, cp, d] = await Promise.all([
      supabase.from('coureurs').select('*, courses(nom)').order('dossard'),
      supabase.from('courses').select('*').order('nom'),
      supabase.from('points_chrono').select('*').order('nom'),
      supabase.from('course_points').select('*'),
      supabase.from('detections').select('*').order('timestamp', { ascending: false }),
    ])
    setCoureurs(c.data || [])
    setCourses(co.data || [])
    setPoints(p.data || [])
    setCoursePoints(cp.data || [])
    setDetections(d.data || [])
    setLoading(false)
  }

  const coureursFiltres = coureurs.filter(c =>
    (!filterCourse || c.course_id === filterCourse) &&
    c.statut !== 'DNS'
  )

  function getDetectionRetenue(coureurId, pointId) {
    const dets = detections.filter(d => d.coureur_id === coureurId && d.point_chrono_id === pointId)
    const forcee = dets.find(d => d.forcee && !d.invalide)
    if (forcee) return forcee
    return dets.filter(d => !d.invalide).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0]
  }

  function getDetectionsBrutes(coureurId, pointId) {
    return detections
      .filter(d => d.coureur_id === coureurId && d.point_chrono_id === pointId)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
  }

  function getPointsParcoursOrdonne(courseId) {
    return coursePoints
      .filter(cp => cp.course_id === courseId)
      .sort((a, b) => a.ordre - b.ordre)
      .map(cp => points.find(p => p.id === cp.point_chrono_id))
      .filter(Boolean)
  }

  function getPointsHorsParcours(coureurId, courseId) {
    const pointsParcours = getPointsParcoursOrdonne(courseId).map(p => p.id)
    const pointsDetectes = [...new Set(
      detections.filter(d => d.coureur_id === coureurId).map(d => d.point_chrono_id)
    )]
    return pointsDetectes
      .filter(pid => !pointsParcours.includes(pid))
      .map(pid => points.find(p => p.id === pid))
      .filter(Boolean)
  }

  function getCoureurAttendusPourPoint(pointId) {
    const courseIds = coursePoints.filter(cp => cp.point_chrono_id === pointId).map(cp => cp.course_id)
    return coureursFiltres.filter(c => {
      if (!courseIds.includes(c.course_id)) return false
      if (c.statut === 'DNF') return !!getDetectionRetenue(c.id, pointId)
      return true
    })
  }

  function getNbBrutes(coureurId, pointId) {
    return detections.filter(d => d.coureur_id === coureurId && d.point_chrono_id === pointId).length
  }

  async function invaliderDetection(detId) {
    await supabase.from('detections').update({
      invalide: true, motif_invalidation: 'Invalide manuellement',
      invalide_par: 'benevole', invalide_le: new Date().toISOString()
    }).eq('id', detId)
    fetchAll()
  }

  async function reactiverDetection(detId) {
    await supabase.from('detections').update({
      invalide: false, motif_invalidation: null, invalide_par: null, invalide_le: null
    }).eq('id', detId)
    fetchAll()
  }

  async function forcerDetection(detId) {
    const det = detections.find(d => d.id === detId)
    if (!det) return
    const autres = detections.filter(d =>
      d.coureur_id === det.coureur_id && d.point_chrono_id === det.point_chrono_id && d.forcee
    )
    for (const a of autres) {
      await supabase.from('detections').update({ forcee: false }).eq('id', a.id)
    }
    await supabase.from('detections').update({ forcee: true, invalide: false }).eq('id', detId)
    fetchAll()
  }

  async function ajouterSaisieManuelle(pointId) {
    if (!selectedCoureur || !pointId || !saisie.heure || !saisie.date) return
    const d = new Date(saisie.date + 'T' + saisie.heure)
    if (isNaN(d.getTime())) return
    await supabase.from('detections').insert({
      coureur_id: selectedCoureur.id, point_chrono_id: pointId,
      timestamp: d.toISOString(), source: 'manuel', forcee: true, invalide: false,
    })
    setSaisie({ date: new Date().toISOString().split('T')[0], heure: '' })
    fetchAll()
  }

  async function changerCourseEnMasse() {
    if (!reglesCourseTarget || selectedCoureurs.length === 0) return
    if (!window.confirm('Changer la course de ' + selectedCoureurs.length + ' coureur(s) ?')) return
    for (const id of selectedCoureurs) {
      await supabase.from('coureurs').update({ course_id: reglesCourseTarget }).eq('id', id)
    }
    setSelectedCoureurs([])
    fetchAll()
  }

  function formatDateTime(ts) {
    if (!ts) return '--'
    return new Date(ts).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    })
  }

  function allerVersCoureur(coureur) {
    setSelectedCoureur(coureur)
    setOnglet('coureur')
    setPopupPoint(null)
    setSearch('')
  }

  const coureursPourRegles = coureurs.filter(c => {
    if (reglesCourse && c.course_id !== reglesCourse) return false
    if (reglesPoint) return !!getDetectionRetenue(c.id, reglesPoint)
    return true
  })

  const detectionsPoint = selectedPoint
    ? detections
        .filter(d => d.point_chrono_id === selectedPoint && !d.invalide)
        .reduce((acc, det) => {
          const coureur = coureursFiltres.find(c => c.id === det.coureur_id)
          if (!coureur) return acc
          const retenue = getDetectionRetenue(det.coureur_id, selectedPoint)
          if (retenue && retenue.id === det.id && !acc.find(a => a.coureur_id === det.coureur_id)) {
            acc.push(det)
          }
          return acc
        }, [])
        .sort((a, b) => triAsc
          ? new Date(a.timestamp) - new Date(b.timestamp)
          : new Date(b.timestamp) - new Date(a.timestamp)
        )
    : []

  const filteredCoureurs = coureursFiltres.filter(c => {
    const q = search.toLowerCase()
    return (
      c.dossard?.toLowerCase().includes(q) ||
      c.nom?.toLowerCase().includes(q) ||
      c.prenom?.toLowerCase().includes(q)
    )
  })

  if (loading) return <div className="loading">Chargement...</div>

  const hasDetail = onglet === 'coureur' && !!selectedCoureur

  return (
    <div className={'pointage' + (hasDetail ? ' has-detail' : '')}>

      <div className="pointage-filter">
        <div className="mode-tabs">
          <button className={'mode-tab ' + (onglet === 'point' ? 'active' : '')} onClick={() => { setOnglet('point'); setSelectedCoureur(null) }}>
            Vue par point
          </button>
          <button className={'mode-tab ' + (onglet === 'coureur' ? 'active' : '')} onClick={() => setOnglet('coureur')}>
            Vue par coureur
          </button>
          <button className={'mode-tab ' + (onglet === 'regles' ? 'active' : '')} onClick={() => setOnglet('regles')}>
            Regles auto
          </button>
        </div>

        <select className="course-filter-select" value={filterCourse} onChange={e => setFilterCourse(e.target.value)}>
          <option value="">Toutes les courses</option>
          {courses.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
        </select>

        {onglet === 'point' && (
          <div className="point-tabs">
            {points
  .filter(p => !filterCourse || coursePoints.some(cp => cp.point_chrono_id === p.id && cp.course_id === filterCourse))
  .map(p => {
              const attendus = getCoureurAttendusPourPoint(p.id)
              const detectes = attendus.filter(c => !!getDetectionRetenue(c.id, p.id))
              const manquants = attendus.filter(c => !getDetectionRetenue(c.id, p.id))
              return (
                <div key={p.id} className="point-tab-wrapper">
                  <button className={'point-tab ' + (selectedPoint === p.id ? 'active' : '')} onClick={() => { setSelectedPoint(p.id); setOngletPoint('arrives') }}>
                    {p.nom}
                  </button>
                  <button className={'point-tab-counter ' + (selectedPoint === p.id ? 'active' : '') + (manquants.length > 0 ? ' has-missing' : '')} onClick={() => setPopupPoint(popupPoint === p.id ? null : p.id)}>
                    {detectes.length}/{attendus.length}
                  </button>
                  {popupPoint === p.id && (
                    <div className="missing-popup">
                      <div className="missing-popup-header">
                        <strong>{manquants.length > 0 ? 'Non detectes a ' + p.nom : 'Tous detectes !'}</strong>
                        <button onClick={() => setPopupPoint(null)}><X size={14}/></button>
                      </div>
                      {manquants.length > 0 && (
                        <div className="missing-popup-list">
                          {manquants.map(c => (
                            <button key={c.id} className="missing-item" onClick={() => allerVersCoureur(c)}>
                              <span className="missing-dossard">#{c.dossard}</span>
                              <span>{c.nom} {c.prenom}</span>
                              <span className="missing-course">{c.courses?.nom}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* VUE PAR POINT */}
      {onglet === 'point' && (
        <div className="pointage-point-view">
          {!selectedPoint ? (
            <div className="detail-empty"><Clock size={40} opacity={0.2}/><p>Selectionnez un point chrono</p></div>
          ) : (() => {
            const attendus = getCoureurAttendusPourPoint(selectedPoint)
            const attenteListe = attendus.filter(c => !getDetectionRetenue(c.id, selectedPoint))
            return (
              <div className="point-detections-card">
                <div className="point-detections-header">
                  <h2>{points.find(p => p.id === selectedPoint)?.nom}</h2>
                  <div style={{display:'flex', alignItems:'center', gap:8}}>
                    <span style={{fontSize:13, color:'#888'}}>{detectionsPoint.length} / {attendus.length}</span>
                    {ongletPoint === 'arrives' && (
                      <button className="btn" style={{background:'#f0f0f0', fontSize:13, display:'flex', alignItems:'center', gap:4}} onClick={() => setTriAsc(!triAsc)}>
                        {triAsc ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                        {triAsc ? 'Plus ancien' : 'Plus recent'}
                      </button>
                    )}
                  </div>
                </div>
                <div className="point-onglets">
                  <button className={'point-onglet ' + (ongletPoint === 'arrives' ? 'active' : '')} onClick={() => setOngletPoint('arrives')}>
                    Arrives ({detectionsPoint.length})
                  </button>
                  <button className={'point-onglet ' + (ongletPoint === 'attendus' ? 'active' : '') + (attenteListe.length > 0 ? ' has-missing' : '')} onClick={() => setOngletPoint('attendus')}>
                    Attendus ({attenteListe.length})
                  </button>
                </div>
                <div className="point-detections-list">
                  {ongletPoint === 'arrives' && (
                    <table className="table">
                      <thead><tr><th>Date / Heure</th><th>Dossard</th><th>Coureur</th><th>Course</th><th>Brutes</th></tr></thead>
                      <tbody>
                        {detectionsPoint.map(det => {
                          const coureur = coureurs.find(c => c.id === det.coureur_id)
                          const nbBrutes = getNbBrutes(det.coureur_id, selectedPoint)
                          return (
                            <tr key={det.id}>
                              <td data-label="Heure"><strong>{formatDateTime(det.timestamp)}</strong></td>
                              <td data-label="Dossard">
                                <button className="dossard-link" onClick={() => coureur && allerVersCoureur(coureur)}>
                                  #{coureur?.dossard}
                                </button>
                              </td>
                              <td data-label="Coureur">{coureur?.nom} {coureur?.prenom}</td>
                              <td data-label="Course">{coureur?.courses?.nom}</td>
                              <td data-label="Brutes">
                                <span style={{fontSize:12, color: nbBrutes > 1 ? '#856404' : '#888'}}>{nbBrutes} brute{nbBrutes > 1 ? 's' : ''}</span>
                                {det.forcee && <span className="forced-badge" style={{marginLeft:4}}>force</span>}
                                {det.source === 'manuel' && <span className="manual-badge" style={{marginLeft:4}}>manuel</span>}
                              </td>
                            </tr>
                          )
                        })}
                        {detectionsPoint.length === 0 && <tr><td colSpan={5} style={{textAlign:'center', color:'#aaa', padding:20}}>Aucun passage enregistre</td></tr>}
                      </tbody>
                    </table>
                  )}
                  {ongletPoint === 'attendus' && (
                    <table className="table">
                      <thead><tr><th>Dossard</th><th>Coureur</th><th>Course</th><th>Statut</th></tr></thead>
                      <tbody>
                        {attenteListe.map(c => (
                          <tr key={c.id}>
                            <td data-label="Dossard"><button className="dossard-link" onClick={() => allerVersCoureur(c)}>#{c.dossard}</button></td>
                            <td data-label="Coureur">{c.nom} {c.prenom}</td>
                            <td data-label="Course">{c.courses?.nom}</td>
                            <td data-label="Statut">{c.statut && <span className={'statut-badge ' + (c.statut === 'DNS' ? 'dns' : 'dnf')}>{c.statut}</span>}</td>
                          </tr>
                        ))}
                        {attenteListe.length === 0 && <tr><td colSpan={4} style={{textAlign:'center', color:'#aaa', padding:20}}>Tous les coureurs sont passes !</td></tr>}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )
          })()}
        </div>
      )}

      {/* VUE PAR COUREUR */}
      {onglet === 'coureur' && (
        <div className="pointage-layout">
          <div className="pointage-list">
            <div className="list-header">
              <div className="search-box">
                <Search size={16} />
                <input placeholder="Nom, prenom, dossard..." value={search} onChange={e => setSearch(e.target.value)} autoFocus />
                {search && <button onClick={() => setSearch('')}><X size={14}/></button>}
              </div>
            </div>
            <div className="list-stats">{filteredCoureurs.length} coureurs</div>
            <div className="list-items">
              {filteredCoureurs.map(coureur => {
                const ptsParcours = getPointsParcoursOrdonne(coureur.course_id)
                const nbDetections = ptsParcours.filter(p => !!getDetectionRetenue(coureur.id, p.id)).length
                return (
                  <div key={coureur.id} className={'coureur-item ' + (selectedCoureur?.id === coureur.id ? 'active' : '')} onClick={() => setSelectedCoureur(coureur)}>
                    <div className="coureur-item-main">
                      <div style={{display:'flex', alignItems:'center', gap:6}}>
                        <span className="coureur-name">{coureur.nom} {coureur.prenom}</span>
                        {coureur.statut && <span className={'statut-badge ' + (coureur.statut === 'DNS' ? 'dns' : 'dnf')}>{coureur.statut}</span>}
                      </div>
                      <span className="coureur-meta">#{coureur.dossard} | {coureur.courses?.nom}</span>
                    </div>
                    {nbDetections > 0 && <span className="detection-count-badge">{nbDetections}/{ptsParcours.length}</span>}
                  </div>
                )
              })}
            </div>
          </div>

          <div className="pointage-detail">
            {!selectedCoureur ? (
              <div className="detail-empty" style={{background:'white', borderRadius:8, height:'100%'}}>
                <Clock size={40} opacity={0.2}/><p>Selectionnez un coureur</p>
              </div>
            ) : (() => {
              const ptsParcours = getPointsParcoursOrdonne(selectedCoureur.course_id)
              const ptsHors = getPointsHorsParcours(selectedCoureur.id, selectedCoureur.course_id)

              const PointBlock = ({ point }) => {
                const retenue = getDetectionRetenue(selectedCoureur.id, point.id)
                const brutes = getDetectionsBrutes(selectedCoureur.id, point.id)
                const showSaisie = showRaw === ('saisie-' + point.id)
                return (
                  <div className="detection-point">
                    <div className="detection-point-header">
                      <div className="detection-point-name">
                        <span className={'status-dot ' + (retenue ? 'ok' : 'missing')}/>
                        <strong>{point.nom}</strong>
                      </div>
                      <div style={{display:'flex', alignItems:'center', gap:8}}>
                        {retenue ? (
                          <span className="time-ok">
                            {retenue.forcee && <span className="forced-badge">force</span>}
                            {retenue.source === 'manuel' && <span className="manual-badge">manuel</span>}
                            {formatDateTime(retenue.timestamp)}
                          </span>
                        ) : <span className="time-missing">--</span>}
                        <button className="btn-small success" onClick={() => setShowRaw(showSaisie ? null : 'saisie-' + point.id)}>
                          + Forcer
                        </button>
                      </div>
                    </div>
                    {showSaisie && (
                      <div className="saisie-manuelle">
                        <input type="date" value={saisie.date} onChange={e => setSaisie({...saisie, date: e.target.value})} />
                        <input type="time" step="1" value={saisie.heure} onChange={e => setSaisie({...saisie, heure: e.target.value})} />
                        <button className="btn success" onClick={() => ajouterSaisieManuelle(point.id)}>Enregistrer</button>
                        <button className="btn" style={{background:'#eee'}} onClick={() => setShowRaw(null)}>Annuler</button>
                      </div>
                    )}
                    {brutes.length > 0 && (
                      <div className="brutes-section">
                        <button className="brutes-toggle" onClick={() => setShowRaw(showRaw === point.id ? null : point.id)}>
                          {brutes.length} detection(s) brute(s)
                          {showRaw === point.id ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                        </button>
                        {showRaw === point.id && (
                          <div className="brutes-list">
                            {brutes.map(det => (
                              <div key={det.id} className={'brute-item ' + (det.invalide ? 'invalide' : '') + ' ' + (det.forcee ? 'forcee' : '')}>
                                <div className="brute-info">
                                  <span className="brute-time">{formatDateTime(det.timestamp)}</span>
                                  <span className="brute-source">{det.source}</span>
                                  {det.forcee && <span className="forced-badge">force</span>}
                                  {det.invalide && <span className="invalid-badge">invalide</span>}
                                  {det.motif_invalidation && <span className="brute-motif">{det.motif_invalidation}</span>}
                                </div>
                                <div className="brute-actions">
                                  {!det.invalide ? (
                                    <>
                                      {!det.forcee && <button className="btn-small success" onClick={() => forcerDetection(det.id)}>Forcer</button>}
                                      <button className="btn-small danger" onClick={() => invaliderDetection(det.id)}>Invalider</button>
                                    </>
                                  ) : (
                                    <button className="btn-small success" onClick={() => reactiverDetection(det.id)}>Reactiver</button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              }

              return (
                <div className="detail-card">
                  <div className="detail-header">
                    <button className="mobile-back-btn" style={{display:'none'}} onClick={() => setSelectedCoureur(null)}>
                      {String.fromCodePoint(0x2190)}
                    </button>
                    <h2>{selectedCoureur.nom} {selectedCoureur.prenom}</h2>
                    <button className="btn-icon desktop-only-close" onClick={() => setSelectedCoureur(null)}><X size={18}/></button>
                  </div>
                  <div className="detail-meta">
                    <span className="badge">#{selectedCoureur.dossard}</span>
                    <span className="badge">{selectedCoureur.courses?.nom}</span>
                    {selectedCoureur.statut && <span className={'statut-badge ' + (selectedCoureur.statut === 'DNS' ? 'dns' : 'dnf')}>{selectedCoureur.statut}</span>}
                  </div>

                  <div className="detections-list">
                    {ptsParcours.map(point => <PointBlock key={point.id} point={point} />)}
                    {ptsHors.length > 0 && (
                      <>
                        <div className="hors-parcours-separator"><span>Points hors parcours</span></div>
                        {ptsHors.map(point => <PointBlock key={point.id} point={point} />)}
                      </>
                    )}
                  </div>
                </div>
              )
            })()}
          </div>
        </div>
      )}

      {/* REGLES AUTO */}
      {onglet === 'regles' && (
        <div className="regles-auto">
          <div className="regles-header">
            <h2>Filtres et actions en masse</h2>
            <p style={{fontSize:13, color:'#888', marginTop:4}}>
              Filtrez les coureurs par course et par point de detection, puis appliquez une action en masse.
            </p>
          </div>
          <div className="regles-filtres">
            <div className="field-row">
              <label>Course actuelle</label>
              <select value={reglesCourse} onChange={e => { setReglesCourse(e.target.value); setSelectedCoureurs([]) }}>
                <option value="">Toutes les courses</option>
                {courses.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
            <div className="field-row">
              <label>A une detection sur</label>
              <select value={reglesPoint} onChange={e => { setReglesPoint(e.target.value); setSelectedCoureurs([]) }}>
                <option value="">Tous les points</option>
                {points.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
              </select>
            </div>
          </div>
          <div className="regles-table-wrap">
            <div className="regles-table-header">
              <span>{coureursPourRegles.length} coureur(s) correspondant(s)</span>
              <button className="btn-small success" onClick={() => {
                if (selectedCoureurs.length === coureursPourRegles.length) setSelectedCoureurs([])
                else setSelectedCoureurs(coureursPourRegles.map(c => c.id))
              }}>
                {selectedCoureurs.length === coureursPourRegles.length ? 'Tout deselectionner' : 'Tout selectionner'}
              </button>
            </div>
            <table className="table">
              <thead>
                <tr>
                  <th style={{width:32}}></th>
                  <th>Dossard</th>
                  <th>Nom</th>
                  <th>Course actuelle</th>
                  {reglesPoint && <th>Detection sur {points.find(p => p.id === reglesPoint)?.nom}</th>}
                </tr>
              </thead>
              <tbody>
                {coureursPourRegles.map(c => {
                  const det = reglesPoint ? getDetectionRetenue(c.id, reglesPoint) : null
                  return (
                    <tr key={c.id} style={{background: selectedCoureurs.includes(c.id) ? '#e8f5f0' : ''}}>
                      <td data-label="">
                        <input type="checkbox" checked={selectedCoureurs.includes(c.id)} onChange={e => {
                          if (e.target.checked) setSelectedCoureurs(prev => [...prev, c.id])
                          else setSelectedCoureurs(prev => prev.filter(id => id !== c.id))
                        }} />
                      </td>
                      <td data-label="Dossard"><strong>#{c.dossard}</strong></td>
                      <td data-label="Nom">{c.nom} {c.prenom}</td>
                      <td data-label="Course">{c.courses?.nom}</td>
                      {reglesPoint && (
                        <td data-label="Detection">{det ? <span style={{color:'#1a7a5e', fontSize:12}}>{formatDateTime(det.timestamp)}</span> : <span style={{color:'#aaa'}}>--</span>}</td>
                      )}
                    </tr>
                  )
                })}
                {coureursPourRegles.length === 0 && <tr><td colSpan={5} style={{textAlign:'center', color:'#aaa', padding:20}}>Aucun coureur correspondant</td></tr>}
              </tbody>
            </table>
          </div>
          {selectedCoureurs.length > 0 && (
            <div className="regles-action">
              <strong>{selectedCoureurs.length} coureur(s) selectionne(s)</strong>
              <div style={{display:'flex', alignItems:'center', gap:10, marginTop:10, flexWrap:'wrap'}}>
                <label style={{fontSize:14}}>Changer vers la course :</label>
                <select value={reglesCourseTarget} onChange={e => setReglesCourseTarget(e.target.value)}>
                  <option value="">Choisir une course...</option>
                  {courses.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
                <button className="btn success" onClick={changerCourseEnMasse} disabled={!reglesCourseTarget}>Appliquer</button>
                <button className="btn" style={{background:'#eee'}} onClick={() => setSelectedCoureurs([])}>Annuler selection</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
